/**
 * CashFlowAnalysis - Kassaflödesanalys enligt indirekt metod (K3/ÅRL)
 * 
 * Visar kassaflödesanalys strukturerad enligt BAS:
 * - Löpande verksamheten
 * - Investeringsverksamheten
 * - Finansieringsverksamheten
 */

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AlertCircle, CheckCircle, TrendingUp, TrendingDown, Minus, AlertTriangle, ChevronDown } from 'lucide-react';
import { calculateCashFlow, type CashFlowData } from '@/lib/cashflow-calculator';
import { formatAmountWithDisplayFormat, determineOptimalUnit, type DisplayUnit, type DisplayFormat } from './utils';
import type { SIEProcessedData } from './types';

interface CashFlowAnalysisProps {
  currentYear: SIEProcessedData;
  previousYear: SIEProcessedData;
  displayFormat?: DisplayFormat;
}

function CashFlowRow({ 
  label, 
  amount, 
  isSubtotal = false,
  isTotal = false,
  indent = 0,
  displayFormat,
  autoUnit,
}: { 
  label: string; 
  amount: number;
  isSubtotal?: boolean;
  isTotal?: boolean;
  indent?: number;
  displayFormat: DisplayFormat;
  autoUnit: DisplayUnit;
}) {
  const baseClass = isTotal 
    ? 'font-bold text-base bg-muted/50' 
    : isSubtotal 
      ? 'font-semibold border-t border-border/50' 
      : '';
  
  const paddingLeft = indent * 16;
  
  return (
    <div className={`flex justify-between py-2 px-3 ${baseClass}`}>
      <span style={{ paddingLeft }} className={isTotal ? '' : 'text-sm'}>
        {label}
      </span>
      <span className={`tabular-nums whitespace-nowrap ${amount < 0 ? 'text-destructive' : ''}`}>
        {formatAmountWithDisplayFormat(amount, displayFormat, autoUnit)}
      </span>
    </div>
  );
}

function SectionHeader({ title, icon }: { title: string; icon?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 py-3 px-3 bg-muted font-semibold text-sm uppercase tracking-wide">
      {icon}
      {title}
    </div>
  );
}

export function CashFlowAnalysis({ currentYear, previousYear, displayFormat = 'auto' }: CashFlowAnalysisProps) {
  const cashFlow = useMemo(() => {
    return calculateCashFlow(currentYear, previousYear);
  }, [currentYear, previousYear]);

  const { operatingActivities, investingActivities, financingActivities } = cashFlow;

  // Determine optimal display unit for consistent formatting (used when displayFormat is 'auto')
  const autoUnit: DisplayUnit = useMemo(() => {
    const allAmounts = [
      operatingActivities.total,
      operatingActivities.operatingResult,
      operatingActivities.depreciation,
      investingActivities.total,
      financingActivities.total,
      cashFlow.yearCashFlow,
      cashFlow.openingCash,
      cashFlow.closingCash,
    ];
    return determineOptimalUnit(allAmounts);
  }, [operatingActivities, investingActivities, financingActivities, cashFlow]);

  // Helper to format amounts with selected format
  const fmt = (amount: number) => formatAmountWithDisplayFormat(amount, displayFormat, autoUnit);

  // Determine overall health indicators
  const isOperatingPositive = operatingActivities.total > 0;
  const isCashFlowPositive = cashFlow.yearCashFlow > 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Löpande verksamheten</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {operatingActivities.total >= 0 ? (
                <TrendingUp className="h-5 w-5 text-success" />
              ) : (
                <TrendingDown className="h-5 w-5 text-destructive" />
              )}
              <span className={`text-xl font-bold whitespace-nowrap ${operatingActivities.total < 0 ? 'text-destructive' : ''}`}>
                {fmt(operatingActivities.total)}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Investeringar</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {investingActivities.total >= 0 ? (
                <TrendingUp className="h-5 w-5 text-success" />
              ) : (
                <Minus className="h-5 w-5 text-muted-foreground" />
              )}
              <span className={`text-xl font-bold whitespace-nowrap ${investingActivities.total < 0 ? 'text-muted-foreground' : ''}`}>
                {fmt(investingActivities.total)}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Finansiering</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {financingActivities.total >= 0 ? (
                <TrendingUp className="h-5 w-5 text-success" />
              ) : (
                <TrendingDown className="h-5 w-5 text-warning" />
              )}
              <span className={`text-xl font-bold whitespace-nowrap`}>
                {fmt(financingActivities.total)}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Årets kassaflöde</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {cashFlow.yearCashFlow >= 0 ? (
                <CheckCircle className="h-5 w-5 text-success" />
              ) : (
                <AlertCircle className="h-5 w-5 text-destructive" />
              )}
              <span className={`text-xl font-bold whitespace-nowrap ${cashFlow.yearCashFlow < 0 ? 'text-destructive' : 'text-success'}`}>
                {fmt(cashFlow.yearCashFlow)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Cash Flow Statement */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Kassaflödesanalys</CardTitle>
              <CardDescription>
                Indirekt metod enligt K3/ÅRL
              </CardDescription>
            </div>
            <Badge variant={cashFlow.isBalanced ? 'secondary' : 'destructive'}>
              {cashFlow.isBalanced ? (
                <>
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Balanserar
                </>
              ) : (
                <>
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Differens: {fmt(cashFlow.cashFlowCheck)}
                </>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {/* DEN LÖPANDE VERKSAMHETEN */}
            <SectionHeader title="Den löpande verksamheten" />
            
            <CashFlowRow label="Rörelseresultat" amount={operatingActivities.operatingResult} displayFormat={displayFormat} autoUnit={autoUnit} />
            
            <div className="py-1 px-3 text-xs text-muted-foreground uppercase tracking-wide">
              Justering för poster som inte ingår i kassaflödet
            </div>
            
            <CashFlowRow label="Avskrivningar" amount={operatingActivities.depreciation} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            <CashFlowRow label="Avsättningar" amount={operatingActivities.provisionsChange} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            {operatingActivities.otherNonCashItems !== 0 && (
              <CashFlowRow label="Övriga ej kassapåverkande poster" amount={operatingActivities.otherNonCashItems} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            )}
            
            <CashFlowRow label="Summa" amount={operatingActivities.subtotalBeforeWorkingCapital} isSubtotal displayFormat={displayFormat} autoUnit={autoUnit} />
            
            <CashFlowRow label="Erhållen ränta" amount={operatingActivities.interestReceived} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            <CashFlowRow label="Erlagd ränta" amount={-operatingActivities.interestPaid} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            <CashFlowRow label="Betald inkomstskatt" amount={-operatingActivities.taxPaid} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            
            <CashFlowRow 
              label="Kassaflöde före förändring av rörelsekapital" 
              amount={operatingActivities.cashFlowBeforeWorkingCapital} 
              isSubtotal
              displayFormat={displayFormat}
              autoUnit={autoUnit}
            />
            
            <div className="py-1 px-3 text-xs text-muted-foreground uppercase tracking-wide">
              Förändring av rörelsekapital
            </div>
            
            <CashFlowRow label="Varulager" amount={operatingActivities.inventoryChange} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            <CashFlowRow label="Kundfordringar" amount={operatingActivities.receivablesChange} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            <CashFlowRow label="Övriga kortfristiga fordringar" amount={operatingActivities.otherCurrentAssetsChange} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            <CashFlowRow label="Leverantörsskulder" amount={operatingActivities.payablesChange} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            <CashFlowRow label="Övriga kortfristiga skulder" amount={operatingActivities.otherCurrentLiabilitiesChange} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            
            <CashFlowRow 
              label="KASSAFLÖDE FRÅN DEN LÖPANDE VERKSAMHETEN" 
              amount={operatingActivities.total} 
              isTotal
              displayFormat={displayFormat}
              autoUnit={autoUnit}
            />

            {/* INVESTERINGSVERKSAMHETEN */}
            <SectionHeader title="Investeringsverksamheten" />
            
            {investingActivities.tangibleAssetsPurchase !== 0 && (
              <CashFlowRow label="Förvärv av materiella anläggningstillgångar" amount={investingActivities.tangibleAssetsPurchase} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            )}
            {investingActivities.tangibleAssetsSale !== 0 && (
              <CashFlowRow label="Försäljning av materiella anläggningstillgångar" amount={investingActivities.tangibleAssetsSale} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            )}
            {investingActivities.intangibleAssetsChange !== 0 && (
              <CashFlowRow label="Förändring immateriella tillgångar" amount={investingActivities.intangibleAssetsChange} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            )}
            {investingActivities.financialAssetsChange !== 0 && (
              <CashFlowRow label="Förändring finansiella anläggningstillgångar" amount={investingActivities.financialAssetsChange} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            )}
            {investingActivities.total === 0 && (
              <div className="py-2 px-3 text-sm text-muted-foreground italic">
                Inga investeringar under perioden
              </div>
            )}
            
            <CashFlowRow 
              label="KASSAFLÖDE FRÅN INVESTERINGSVERKSAMHETEN" 
              amount={investingActivities.total} 
              isTotal
              displayFormat={displayFormat}
              autoUnit={autoUnit}
            />

            {/* FINANSIERINGSVERKSAMHETEN */}
            <SectionHeader title="Finansieringsverksamheten" />
            
            {financingActivities.newEquity !== 0 && (
              <CashFlowRow label="Nyemission/aktieägartillskott" amount={financingActivities.newEquity} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            )}
            {financingActivities.dividendPaid !== 0 && (
              <CashFlowRow label="Utbetald utdelning" amount={-financingActivities.dividendPaid} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            )}
            {financingActivities.loansReceived !== 0 && (
              <CashFlowRow label="Upptagna lån" amount={financingActivities.loansReceived} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            )}
            {financingActivities.loansRepaid !== 0 && (
              <CashFlowRow label="Amortering av lån" amount={-financingActivities.loansRepaid} indent={1} displayFormat={displayFormat} autoUnit={autoUnit} />
            )}
            {financingActivities.total === 0 && (
              <div className="py-2 px-3 text-sm text-muted-foreground italic">
                Ingen finansieringsaktivitet under perioden
              </div>
            )}
            
            <CashFlowRow 
              label="KASSAFLÖDE FRÅN FINANSIERINGSVERKSAMHETEN" 
              amount={financingActivities.total} 
              isTotal
              displayFormat={displayFormat}
              autoUnit={autoUnit}
            />

            {/* SAMMANSTÄLLNING */}
            <Separator className="my-0" />
            
            <div className="bg-muted/30">
              <CashFlowRow label="ÅRETS KASSAFLÖDE" amount={cashFlow.yearCashFlow} isTotal displayFormat={displayFormat} autoUnit={autoUnit} />
              <CashFlowRow label="Likvida medel vid årets början" amount={cashFlow.openingCash} displayFormat={displayFormat} autoUnit={autoUnit} />
              <div className="border-t-2 border-primary/20">
                <CashFlowRow label="LIKVIDA MEDEL VID ÅRETS SLUT" amount={cashFlow.closingCash} isTotal displayFormat={displayFormat} autoUnit={autoUnit} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Warnings */}
      {financingActivities.dividendPaid > Math.abs(operatingActivities.total) && operatingActivities.total > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Potentiellt fel i utdelningsberäkning</AlertTitle>
          <AlertDescription>
            Beräknad utdelning ({fmt(financingActivities.dividendPaid)}) överstiger kassaflödet från löpande verksamheten ({fmt(operatingActivities.total)}). 
            Kontrollera att detta stämmer eller att det inte rör sig om en felaktig tolkning av eget kapital-förändringar.
          </AlertDescription>
        </Alert>
      )}

      {/* Debug Info */}
      <Collapsible>
        <Card>
          <CardHeader className="py-3">
            <CollapsibleTrigger className="flex items-center justify-between w-full">
              <CardTitle className="text-sm">Debug: Beräkningsdetaljer</CardTitle>
              <ChevronDown className="h-4 w-4" />
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="text-xs font-mono space-y-1 bg-muted/30 p-4">
              <p><strong>Eget kapital (råvärden från SIE):</strong></p>
              <p>Innevarande år: {fmt(cashFlow.debug.rawCurrentEquity)} (negativt = kredit)</p>
              <p>Föregående år: {fmt(cashFlow.debug.rawPreviousEquity)} (negativt = kredit)</p>
              <p>Årets resultat: {fmt(cashFlow.debug.netResultUsed)}</p>
              <p>Innevarande EK exkl. resultat: {fmt(cashFlow.debug.currEquityExResult)}</p>
              <p>EK-förändring exkl. resultat: {fmt(cashFlow.debug.equityChange)}</p>
              <Separator className="my-2" />
              <p><strong>Resultaträkning (normaliserade värden):</strong></p>
              <p>Rörelseresultat: {fmt(cashFlow.debug.operatingResultRaw)}</p>
              <p>Finansiella intäkter: {fmt(cashFlow.debug.financialIncomeRaw)}</p>
              <p>Finansiella kostnader: {fmt(cashFlow.debug.financialExpensesRaw)}</p>
              <p>Resultat efter finansiella poster: {fmt(cashFlow.debug.resultAfterFinancialRaw)}</p>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Noteringar</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            <strong>Metod:</strong> Kassaflödesanalysen är beräknad enligt indirekt metod i enlighet med K3 (BFNAR 2012:1) och Årsredovisningslagen.
          </p>
          <p>
            <strong>Teckenkonvention:</strong> SIE-filer använder debet/kredit-konvention där skulder och eget kapital är negativa. 
            Denna analys normaliserar värdena för presentation.
          </p>
          <p>
            <strong>Utdelning:</strong> Beräknas genom att analysera förändring i konto 2098 (Vinst/förlust föregående år). 
            Andra kapitalöverföringar kan kräva manuell verifikation.
          </p>
          <p>
            <strong>Begränsningar:</strong> Vissa poster kan vara approximationer baserade på nettoförändringar i balansräkningen. 
            För exakt kassaflödesanalys krävs detaljerad transaktionsdata.
          </p>
          {!cashFlow.isBalanced && (
            <p className="text-warning">
              <strong>Differens:</strong> Det finns en differens på {fmt(cashFlow.cashFlowCheck)} mellan beräknat kassaflöde och faktisk förändring i likvida medel. 
              Detta kan bero på avrundningar eller poster som inte fångas av analysen.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
